/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

(function () {
  'use strict';

  var LIMITED_VARIANT_ID = '42006568108103';
  var MAX_PER_ORDER = 48;
  var LIMIT_MESSAGE = 'A maximum of 48 TENPOWER 50XG cells may be purchased per order while supply is limited.';
  var originalFetch = window.fetch.bind(window);
  var checkoutBypass = false;
  var refreshTimer;

  function asPositiveInteger(value, fallback) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  function isLimitedVariant(value) {
    if (value === null || value === undefined) {
      return false;
    }

    return String(value).split(':')[0] === LIMITED_VARIANT_ID;
  }

  function requestPath(input) {
    try {
      return new URL(typeof input === 'string' ? input : input.url, window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  }

  function limitResponse() {
    return new Response(JSON.stringify({
      status: 422,
      message: 'Cart Error',
      description: LIMIT_MESSAGE
    }), {
      status: 422,
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  function readFlatPayload(params) {
    var payload = {};

    params.forEach(function (value, key) {
      payload[key] = value;
    });

    return payload;
  }

  async function readRequestPayload(input, init) {
    var body = init && init.body;

    if (body === undefined && typeof Request !== 'undefined' && input instanceof Request) {
      body = await input.clone().text();
    }

    if (body instanceof FormData || body instanceof URLSearchParams) {
      return readFlatPayload(body);
    }

    if (typeof body !== 'string' || body.trim() === '') {
      return {};
    }

    try {
      return JSON.parse(body);
    } catch (error) {
      return readFlatPayload(new URLSearchParams(body));
    }
  }

  function targetAddQuantity(payload) {
    if (Array.isArray(payload.items)) {
      return payload.items.reduce(function (total, item) {
        return total + (isLimitedVariant(item.id) ? asPositiveInteger(item.quantity, 1) : 0);
      }, 0);
    }

    if (isLimitedVariant(payload.id || payload.variant_id)) {
      return asPositiveInteger(payload.quantity, 1);
    }

    var groupedItems = {};

    Object.keys(payload).forEach(function (key) {
      var match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);

      if (match) {
        groupedItems[match[1]] = groupedItems[match[1]] || {};
        groupedItems[match[1]][match[2]] = payload[key];
      }
    });

    return Object.keys(groupedItems).reduce(function (total, key) {
      var item = groupedItems[key];
      return total + (isLimitedVariant(item.id) ? asPositiveInteger(item.quantity, 1) : 0);
    }, 0);
  }

  function targetCartQuantity(cart) {
    return cart.items.reduce(function (total, item) {
      return total + (isLimitedVariant(item.variant_id) ? item.quantity : 0);
    }, 0);
  }

  async function getCart() {
    var response = await originalFetch(window.routes.cartUrl + '.js', {
      credentials: 'same-origin',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to verify cart quantity.');
    }

    return response.json();
  }

  function itemForChange(payload, cart) {
    if (payload.line !== undefined) {
      return cart.items[asPositiveInteger(payload.line, 0) - 1];
    }

    var requestedId = payload.id || payload.key;

    return cart.items.find(function (item) {
      return String(item.key) === String(requestedId) ||
        String(item.variant_id) === String(requestedId);
    });
  }

  function targetTotalAfterChange(payload, cart) {
    var item = itemForChange(payload, cart);

    if (!item || !isLimitedVariant(item.variant_id)) {
      return null;
    }

    return targetCartQuantity(cart) - item.quantity + asPositiveInteger(payload.quantity, item.quantity);
  }

  function targetTotalAfterUpdate(payload, cart) {
    var updates = payload.updates || {};
    var total = targetCartQuantity(cart);
    var changed = false;

    Object.keys(payload).forEach(function (key) {
      var match = key.match(/^updates\[(.+)\]$/);

      if (match) {
        updates[match[1]] = payload[key];
      }
    });

    if (Object.keys(updates).length === 0) {
      updates = payload;
    }

    Object.keys(updates).forEach(function (key) {
      var item = cart.items.find(function (candidate, index) {
        return String(candidate.key) === String(key) ||
          String(candidate.variant_id) === String(key) ||
          String(index + 1) === String(key);
      });

      if (item && isLimitedVariant(item.variant_id)) {
        total += asPositiveInteger(updates[key], item.quantity) - item.quantity;
        changed = true;
      }
    });

    return changed ? total : null;
  }

  window.fetch = async function (input, init) {
    var path = requestPath(input);
    var isAdd = /\/cart\/add(?:\.js)?$/.test(path);
    var isChange = /\/cart\/change(?:\.js)?$/.test(path);
    var isUpdate = /\/cart\/update(?:\.js)?$/.test(path);

    if (!isAdd && !isChange && !isUpdate) {
      return originalFetch(input, init);
    }

    try {
      var payload = await readRequestPayload(input, init);
      var cart;
      var resultingQuantity = null;

      if (isAdd) {
        var addedQuantity = targetAddQuantity(payload);

        if (addedQuantity > 0) {
          cart = await getCart();
          resultingQuantity = targetCartQuantity(cart) + addedQuantity;
        }
      } else {
        cart = await getCart();
        resultingQuantity = isChange ?
          targetTotalAfterChange(payload, cart) :
          targetTotalAfterUpdate(payload, cart);
      }

      if (resultingQuantity !== null && resultingQuantity > MAX_PER_ORDER) {
        return limitResponse();
      }
    } catch (error) {
      if (isAdd && targetAddQuantity(payload || {}) > 0) {
        return limitResponse();
      }
    }

    return originalFetch(input, init);
  };

  function showLimitMessage(container) {
    var existing = document.querySelector('[data-diy500amp-50xg-limit-message]');

    if (existing) {
      existing.remove();
    }

    var message = document.createElement('div');
    message.className = 'alert alert--error';
    message.setAttribute('data-diy500amp-50xg-limit-message', '');
    message.setAttribute('role', 'alert');
    message.textContent = LIMIT_MESSAGE;
    (container || document.querySelector('main') || document.body).prepend(message);

    window.setTimeout(function () {
      message.remove();
    }, 6500);
  }

  function applyProductLimit() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      var variantInput = form.querySelector('[name="id"]');

      if (!variantInput || !isLimitedVariant(variantInput.value)) {
        return;
      }

      var quantityInput = form.querySelector('[name="quantity"]');

      if (quantityInput) {
        quantityInput.max = MAX_PER_ORDER;
        quantityInput.setAttribute('data-diy500amp-50xg-limit', '');

        if (asPositiveInteger(quantityInput.value, 1) > MAX_PER_ORDER) {
          quantityInput.value = MAX_PER_ORDER;
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      var paymentButton = form.querySelector('.shopify-payment-button') ||
        form.closest('.product-form')?.querySelector('.shopify-payment-button');

      if (paymentButton) {
        paymentButton.hidden = true;
      }
    });
  }

  function applyCartLimit(cart) {
    var total = targetCartQuantity(cart);

    cart.items.forEach(function (item, index) {
      if (!isLimitedVariant(item.variant_id)) {
        return;
      }

      var line = String(index + 1);
      var lineMaximum = Math.max(0, MAX_PER_ORDER - (total - item.quantity));

      document.querySelectorAll('.quantity-selector__value[data-line="' + line + '"]').forEach(function (input) {
        input.max = lineMaximum;
        input.setAttribute('data-diy500amp-50xg-limit', '');
      });

      document.querySelectorAll('[data-action="increase-quantity"][data-line="' + line + '"]').forEach(function (button) {
        var nextQuantity = asPositiveInteger(button.getAttribute('data-quantity'), item.quantity + 1);
        button.setAttribute('data-quantity', Math.min(nextQuantity, lineMaximum));
        button.disabled = item.quantity >= lineMaximum;
      });
    });

    if (total > 0) {
      document.querySelectorAll('.additional-checkout-buttons, .dynamic-checkout__content').forEach(function (element) {
        element.hidden = true;
      });
    }
  }

  async function refreshLimits() {
    applyProductLimit();

    try {
      applyCartLimit(await getCart());
    } catch (error) {
      // The request guard remains active even if the optional UI refresh fails.
    }
  }

  function scheduleRefresh() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshLimits, 80);
  }

  document.addEventListener('input', function (event) {
    if (!event.target.matches('[data-diy500amp-50xg-limit]')) {
      return;
    }

    if (asPositiveInteger(event.target.value, 1) > MAX_PER_ORDER) {
      event.target.value = MAX_PER_ORDER;
      showLimitMessage(event.target.closest('form'));
    }
  }, true);

  document.addEventListener('submit', function (event) {
    var submitter = event.submitter;

    if (checkoutBypass || !submitter || submitter.name !== 'checkout') {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    getCart().then(function (cart) {
      if (targetCartQuantity(cart) > MAX_PER_ORDER) {
        showLimitMessage(event.target);
        return;
      }

      checkoutBypass = true;

      if (typeof event.target.requestSubmit === 'function') {
        event.target.requestSubmit(submitter);
      } else {
        event.target.submit();
      }
    }).catch(function () {
      showLimitMessage(event.target);
    });
  }, true);

  document.addEventListener('variant:changed', scheduleRefresh);
  document.addEventListener('product:added', scheduleRefresh);
  document.addEventListener('cart:refresh', scheduleRefresh);
  document.addEventListener('DOMContentLoaded', scheduleRefresh);
})();
